import { Link } from "react-router-dom";
import { ArrowRight, Eye, Scan, Droplets, Layers, Stethoscope } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";

const CTA_DARK =
  "inline-flex items-center gap-2 rounded-full bg-[#0D3327] px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-[#174C3A]";

const SECTIONS = [
  {
    id: "cataratta",
    num: "01",
    icon: Eye,
    title: "Chirurgia della cataratta",
    testid: "service-detail-cataract",
    cta: "Richiedi una valutazione",
    paragraphs: [
      "La cataratta consiste nella progressiva perdita di trasparenza del cristallino naturale dell'occhio e può determinare riduzione della qualità visiva, difficoltà nella guida, alterazione della percezione dei colori e maggiore sensibilità alla luce.",
      "L'intervento consiste nella rimozione del cristallino opacizzato e nella sua sostituzione con una lente intraoculare artificiale.",
      "Le tecniche chirurgiche moderne permettono una procedura estremamente precisa e una rapida ripresa visiva, ma la qualità del risultato dipende dalla corretta indicazione, dalla pianificazione dell'intervento e dall'esperienza del chirurgo.",
      "Il Professor Traverso ha maturato nel corso della propria carriera un'esperienza di riferimento nazionale nella chirurgia della cataratta e, più in generale, nella chirurgia del segmento anteriore.",
    ],
  },
  {
    id: "refrattiva",
    num: "02",
    icon: Scan,
    title: "Chirurgia refrattiva",
    testid: "service-detail-refractive",
    cta: "Vorrei sapere se sono idoneo",
    paragraphs: [
      "La chirurgia refrattiva comprende le procedure che hanno l'obiettivo di correggere difetti visivi come miopia, ipermetropia e astigmatismo, riducendo e, nei pazienti idonei, talvolta eliminando la dipendenza da occhiali o lenti a contatto.",
      "A seconda delle caratteristiche dell'occhio, dell'età e del difetto visivo, possono essere utilizzate tecniche laser sulla cornea oppure procedure che coinvolgono il cristallino.",
      "Ogni indicazione deve essere definita dopo una valutazione oftalmologica approfondita e una serie di esami strumentali che permettono di individuare la soluzione più appropriata per il singolo paziente.",
      "Il Professor Traverso è stato tra i precursori della chirurgia refrattiva in Italia e tra i primi specialisti italiani a utilizzare le procedure laser per la correzione dei difetti visivi.",
    ],
  },
  {
    id: "glaucoma",
    num: "03",
    icon: Droplets,
    title: "Chirurgia del glaucoma",
    testid: "service-detail-glaucoma",
    cta: "Richiedi una valutazione",
    paragraphs: [
      "Il glaucoma è una malattia progressiva del nervo ottico, frequentemente associata a una pressione intraoculare superiore a quella che l'occhio è in grado di tollerare.",
      "Poiché nelle fasi iniziali può non dare sintomi, una diagnosi tempestiva e un monitoraggio accurato sono fondamentali.",
      "Quando terapia farmacologica o trattamenti laser non consentono un controllo adeguato, può essere indicato un intervento chirurgico finalizzato a ridurre la pressione intraoculare e proteggere nel tempo il nervo ottico.",
      "Il glaucoma rappresenta uno dei principali campi di attività clinica, chirurgica e scientifica del Professor Traverso.",
    ],
  },
  {
    id: "cornea",
    num: "04",
    icon: Layers,
    title: "Chirurgia della cornea",
    testid: "service-detail-cornea",
    cta: "Richiedi una valutazione",
    paragraphs: [
      "La cornea è la superficie trasparente posta nella parte anteriore dell'occhio e svolge un ruolo fondamentale nella messa a fuoco delle immagini. Diverse patologie possono comprometterne trasparenza, forma o funzione.",
      "Nei casi in cui sia necessario un trattamento chirurgico, oggi è possibile ricorrere a differenti tecniche, comprese procedure di trapianto corneale completo o selettivo dei soli strati interessati dalla patologia.",
      "La cornea rappresenta uno degli ambiti ai quali il Professor Traverso ha dedicato una parte importante della propria attività chirurgica e di ricerca, comprendendo anche gli studi sull'utilizzo delle cellule staminali limbari per la ricostruzione della superficie oculare.",
    ],
  },
  {
    id: "ambulatoriale",
    num: "05",
    icon: Stethoscope,
    title: "Visita oculistica specialistica e diagnostica",
    testid: "service-detail-diagnostics",
    cta: "Prenota / Richiedi una visita",
    paragraphs: [
      "Una valutazione oftalmologica completa non consiste soltanto nella misurazione della vista, ma nell'inquadramento complessivo della salute dell'occhio.",
      "L'attività ambulatoriale del Professor Traverso si fonda su decenni di esperienza clinica e specialistica in Oftalmologia e consente un inquadramento approfondito sia delle problematiche oculari più comuni sia dei quadri clinici più complessi.",
    ],
  },
];

export default function Servizi() {
  return (
    <div data-testid="services-page">
      <PageHero
        testid="services-hero"
        eyebrow="Aree di attività"
        title="Di cosa mi occupo"
        subtitle="Esperienza clinica e chirurgica nella diagnosi e nel trattamento delle principali patologie del segmento anteriore dell'occhio."
      />

      {SECTIONS.map((s, idx) => (
        <section
          key={s.id}
          data-testid={s.testid}
          id={s.id}
          className={idx % 2 === 0 ? "bg-[#F8FAF8]" : "border-y border-[#D5E2DB] bg-white"}
        >
          <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <Reveal>
              <div className="flex items-start gap-6 sm:gap-10">
                <div className="hidden shrink-0 sm:block">
                  <p className="font-mono text-sm text-[#2E6B50]">{s.num}</p>
                  <div className="mt-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#E8F1EC] text-[#174C3A]">
                    <s.icon size={22} />
                  </div>
                </div>
                <div className="min-w-0">
                  <h2 className="font-serif text-2xl font-semibold leading-tight text-[#0D3327] sm:text-3xl">
                    {s.title}
                  </h2>
                  <div className="mt-6 space-y-4 text-base leading-relaxed text-[#4B5852]">
                    {s.paragraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                  <Link to="/richiedi-una-valutazione" data-testid={`${s.testid}-cta`} className={`${CTA_DARK} mt-8`}>
                    {s.cta} <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      ))}
    </div>
  );
}
